import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Gavel, Zap as Lightning, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { loginRedirect } from "@/components/Layout";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);
  if (!target) return null;
  const diff = new Date(target).getTime() - now;
  if (diff <= 0) return "ended";
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return `${h}h ${m}m ${s}s`;
}

export default function AuctionPanel({ listing, onUpdate }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const countdown = useCountdown(listing.ends_at);
  const min = (listing.current_bid ?? listing.starting_bid ?? 0) + (listing.current_bid ? (listing.min_increment || 1) : 0);
  const [bid, setBid] = useState(min);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setBid(min); }, [min]);
  useEffect(() => { api.get(`/listings/${listing.listing_id}/bids`).then(r => setBids(r.data)); }, [listing.listing_id, listing.current_bid]);

  const placeBid = async () => {
    if (!user) return loginRedirect();
    if (Number(bid) < min) { toast.error(`Minimum bid is ₹${min}`); return; }
    setLoading(true);
    try {
      await api.post(`/listings/${listing.listing_id}/bid`, { amount: Number(bid) });
      toast.success("Bid placed!");
      onUpdate && onUpdate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bid failed");
    } finally { setLoading(false); }
  };

  const buyNow = async () => {
    if (!user) return loginRedirect();
    if (!confirm(`Confirm Buy Now at ₹${listing.buy_now_price.toLocaleString("en-IN")}?`)) return;
    try {
      const r = await api.post(`/listings/${listing.listing_id}/buy-now`);
      toast.success("Purchased!");
      navigate(`/track/${r.data.booking_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purchase failed");
    }
  };

  if (listing.sold) return <div className="mt-4 p-4 bg-emerald-50 rounded-2xl text-emerald-800 font-bold text-center">Sold</div>;

  return (
    <div className="mt-4 space-y-3">
      <div className="p-4 rounded-2xl bg-gradient-to-br from-[#DDA77B]/25 to-[#E05E36]/10 border border-[#DDA77B]/40">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-stone-600"><Gavel size={14} /> Live auction</div>
        <div className="mt-1 flex items-end justify-between gap-3">
          <div>
            <div className="text-xs text-stone-600">Current bid</div>
            <div className="heading text-3xl font-extrabold text-stone-900" data-testid="current-bid">
              ₹{Number(listing.current_bid || listing.starting_bid || 0).toLocaleString("en-IN")}
            </div>
            <div className="text-[11px] text-stone-500">{listing.bid_count || 0} bids · min next ₹{min.toLocaleString("en-IN")}</div>
          </div>
          <div className="text-right">
            <div className="text-xs text-stone-600 flex items-center gap-1"><Clock size={12} /> Ends</div>
            <motion.div key={countdown} className="heading text-lg font-bold text-[#E05E36]" data-testid="auction-countdown">{countdown || "—"}</motion.div>
          </div>
        </div>
      </div>

      <div className="flex gap-2">
        <Input type="number" value={bid} onChange={e => setBid(e.target.value)} className="rounded-full" data-testid="bid-input" />
        <Button onClick={placeBid} disabled={loading} className="rounded-full bg-[#2D5A27] hover:bg-[#24491f] gap-1" data-testid="place-bid">
          <Gavel size={16} /> Bid
        </Button>
      </div>

      {listing.buy_now_price && (
        <Button onClick={buyNow} className="w-full rounded-full bg-[#E05E36] hover:bg-[#c94e2a] gap-2" data-testid="buy-now">
          <Lightning size={16} /> Buy Now — ₹{Number(listing.buy_now_price).toLocaleString("en-IN")}
        </Button>
      )}

      {bids.length > 0 && (
        <div className="mt-3">
          <div className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-2">Recent bids</div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {bids.slice(0, 8).map(b => (
              <div key={b.bid_id} className="flex justify-between text-xs text-stone-600 px-2 py-1 rounded bg-stone-50">
                <span>{b.bidder_name}</span>
                <span className="font-bold text-stone-900">₹{Number(b.amount).toLocaleString("en-IN")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
